import NavButton, { NavButtonRef } from '@atoms/NavButton/NavButton';
import ShadowContainerHorizontal from '@atoms/ShadowContainerHorizontal/ShadowContainerHorizontal';
import BreakpointEnum from '@models/enums/BreakpointEnum';
import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import HorizontalDirectionEnum from '@models/enums/HorizontalDirectionEnum';
import Carousel from '@molecules/Carousel/Carousel';
import duplicateArrayItems from '@utils/helpers/duplicateArrayItems';
import useBreakpoint from '@utils/hooks/useBreakpoint';
import useCarouselScrollAnimation from '@utils/hooks/useCarouselScrollAnimation';
import useIsTouchDevice from '@utils/hooks/useIsTouchDevice';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { ReactNode, RefObject, useEffect, useRef, useState } from 'react';
import SwiperType from 'swiper';
import { FreeMode } from 'swiper/modules';
import { Swiper, SwiperOptions } from 'swiper/types';

interface Props<T> {
  readonly dataTestId?: string;
  readonly id: string;
  readonly loop?: boolean;
  readonly title?: string | JSX.Element;
  readonly titleClassName?: string;
  readonly titleWrapperClassName?: string;
  readonly horizontalSpacingClassName?: string;
  readonly items: T[];
  readonly shadows?: boolean;
  readonly shadowColour?: string;
  readonly slidesToShowDesktop?: number;
  readonly slidesToShowTablet?: number;
  readonly slidesToShowMobile?: number;
  readonly desktopBreakpoint?: BreakpointEnum.Medium | BreakpointEnum.XLarge;
  readonly hideHeaderOnMobile?: boolean;
  readonly additionalBreakpoints?: { [width: number]: SwiperOptions };
  readonly spaceBetween?: number;
  readonly shouldReinitializeNavigation?: boolean; // for when the carousel is used inside a portal (Modal or Drawer)
  readonly isScrollAnimationEnabled?: boolean;
  readonly isLoadedInView?: boolean;
  readonly isLightMode?: boolean;
  readonly isOverflowHidden?: boolean;
  readonly epiEdit?: string;
  readonly children: (itemsToDisplay: T[]) => ReactNode;
}

const NavigationCarousel = <T,>({
  dataTestId = 'Navigation-Carousel',
  id,
  loop = true,
  title,
  titleClassName = 'h2',
  titleWrapperClassName,
  horizontalSpacingClassName = 'container',
  items,
  shadows = false,
  shadowColour,
  slidesToShowDesktop = 3,
  slidesToShowTablet = 2,
  slidesToShowMobile = 1.1,
  desktopBreakpoint = BreakpointEnum.XLarge,
  hideHeaderOnMobile,
  additionalBreakpoints = {},
  spaceBetween = 20,
  shouldReinitializeNavigation,
  isScrollAnimationEnabled,
  isLoadedInView,
  isLightMode,
  isOverflowHidden = true,
  epiEdit,
  children,
}: Props<T>) => {
  const [swiperInstance, setSwiperInstance] = useState<Swiper>();
  const [isSwiperInitialized, setIsSwiperInitialized] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const [currentSlidesPerView, setCurrentSlidesPerView] = useState(0);
  const [shouldLoop, setShouldLoop] = useState(false);
  const screenBreakpoint = useBreakpoint(BreakpointTypeEnum.Screen);
  const viewportBreakpoint = useBreakpoint();
  const isTouchDevice = useIsTouchDevice();
  const leftButtonRef = useRef<NavButtonRef>(null);
  const rightButtonRef = useRef<NavButtonRef>(null);
  const hasMoreItemsToShow =
    currentSlidesPerView > 0 && items.length > currentSlidesPerView;

  const isMobile = useMediaQuery(1024);
  const isDesktopScreen =
    (desktopBreakpoint === BreakpointEnum.XLarge &&
      screenBreakpoint === BreakpointEnum.XLarge) ||
    (desktopBreakpoint === BreakpointEnum.Medium &&
      screenBreakpoint !== BreakpointEnum.Small);
  const shouldDisplayShadowsOnBreakpoint =
    (desktopBreakpoint === BreakpointEnum.XLarge &&
      viewportBreakpoint !== BreakpointEnum.XLarge) ||
    (desktopBreakpoint === BreakpointEnum.Medium &&
      viewportBreakpoint === BreakpointEnum.Small);

  const initialTabletSlidesClassname = {
    2: 'md:[&>.swiper-wrapper>.swiper-slide]:w-1/2',
    3: 'md:[&>.swiper-wrapper>.swiper-slide]:w-1/3',
    4: 'md:[&>.swiper-wrapper>.swiper-slide]:w-1/4',
  }[slidesToShowTablet];

  const initialDesktopSlidesClassname = {
    3: 'xl:[&>.swiper-wrapper>.swiper-slide]:w-1/3',
    4: 'xl:[&>.swiper-wrapper>.swiper-slide]:w-1/4',
    5: 'xl:[&>.swiper-wrapper>.swiper-slide]:w-1/5',
    6: 'xl:[&>.swiper-wrapper>.swiper-slide]:w-1/6',
  }[slidesToShowDesktop];

  const itemsMultiplier = Math.ceil(currentSlidesPerView + 1) * 2;
  const itemsToDisplay = shouldLoop
    ? duplicateArrayItems(items, itemsMultiplier)
    : items;
  const isLeftNavButtonDisabled = !shouldLoop && slideIndex === 0;
  const isRightNavButtonDisabled =
    !shouldLoop && slideIndex >= itemsToDisplay.length - currentSlidesPerView;

  const handleSwiperUpdate = (swiper: SwiperType) => {
    const slidesPerView = swiper?.params?.slidesPerView;

    setCurrentSlidesPerView(
      slidesPerView && typeof slidesPerView === 'number' ? slidesPerView : 0
    );
  };

  const handleNavButtonClick = ({
    activeButtonRef,
    inactiveButtonRef,
    newSlideIndex,
  }: {
    activeButtonRef: RefObject<NavButtonRef>;
    inactiveButtonRef: RefObject<NavButtonRef>;
    newSlideIndex: number;
  }) => {
    setTimeout(() => {
      const isActiveButtonDisabled = activeButtonRef.current?.disabled;
      const isInactiveButtonEnabled = !inactiveButtonRef.current?.disabled;

      if (isActiveButtonDisabled && isInactiveButtonEnabled) {
        inactiveButtonRef.current?.focus();
      }
    });

    if (isScrollAnimationEnabled) {
      swiperInstance?.slideTo(newSlideIndex);
    }
  };

  useEffect(() => {
    const shouldLoopCondition = isScrollAnimationEnabled
      ? items.length <= 4 && viewportBreakpoint === BreakpointEnum.Small
      : ((desktopBreakpoint === BreakpointEnum.XLarge && !isTouchDevice) ||
          (desktopBreakpoint === BreakpointEnum.Medium &&
            !!viewportBreakpoint &&
            viewportBreakpoint !== BreakpointEnum.Small)) &&
        hasMoreItemsToShow &&
        loop;

    setShouldLoop(shouldLoopCondition);
  }, [
    viewportBreakpoint,
    desktopBreakpoint,
    isTouchDevice,
    hasMoreItemsToShow,
    loop,
    items.length,
    isScrollAnimationEnabled,
  ]);

  useEffect(() => {
    swiperInstance?.slideTo(0, 0);
    if (shouldReinitializeNavigation) swiperInstance?.navigation?.init();
    swiperInstance?.navigation.update();
  }, [swiperInstance, shouldLoop, shouldReinitializeNavigation]);

  useCarouselScrollAnimation({
    swiperInstance,
    shouldAnimateCondition: !!(
      isLoadedInView &&
      isScrollAnimationEnabled &&
      !isMobile
    ),
  });

  return (
    <div
      className="overflow-swiper w-full"
      data-testid={dataTestId}
      data-epi-property-name={epiEdit}
    >
      <div
        className={classNames('pb-7.5', {
          'xl:pb-10': desktopBreakpoint === BreakpointEnum.XLarge,
          'md:pb-10': desktopBreakpoint === BreakpointEnum.Medium,
          hidden:
            (hideHeaderOnMobile &&
              desktopBreakpoint === BreakpointEnum.XLarge &&
              screenBreakpoint !== BreakpointEnum.XLarge) ||
            (hideHeaderOnMobile &&
              desktopBreakpoint === BreakpointEnum.Medium &&
              screenBreakpoint === BreakpointEnum.Small),
          [`${horizontalSpacingClassName}`]: !isScrollAnimationEnabled,
        })}
      >
        <div
          className={classNames(
            'flex w-full flex-row items-end',
            title ? 'justify-between' : 'justify-end',
            titleWrapperClassName
          )}
        >
          {title && (
            <motion.h2
              {...(isScrollAnimationEnabled
                ? {
                    initial: { opacity: 0, y: 75 },
                    animate: isLoadedInView ? { opacity: 1, y: 0 } : {},
                    transition: { duration: 0.7, ease: 'easeInOut' },
                  }
                : {})}
              className={titleClassName}
              data-testid={`${dataTestId}-Title`}
            >
              {title}
            </motion.h2>
          )}

          <motion.div
            {...(isScrollAnimationEnabled
              ? {
                  initial: { opacity: 0 },
                  animate: isLoadedInView ? { opacity: 1 } : {},
                  transition: { duration: 0.75, delay: 0.5 },
                }
              : {})}
            className={
              hasMoreItemsToShow && isDesktopScreen
                ? 'flex flex-row gap-5'
                : 'hidden'
            }
          >
            <NavButton
              ref={leftButtonRef}
              direction={HorizontalDirectionEnum.Left}
              {...(isScrollAnimationEnabled ? {} : { id })}
              disabled={isLeftNavButtonDisabled}
              onClick={() =>
                handleNavButtonClick({
                  activeButtonRef: leftButtonRef,
                  inactiveButtonRef: rightButtonRef,
                  newSlideIndex: slideIndex - 1,
                })
              }
              isLightMode={isLightMode}
            />

            <NavButton
              ref={rightButtonRef}
              direction={HorizontalDirectionEnum.Right}
              {...(isScrollAnimationEnabled ? {} : { id })}
              disabled={isRightNavButtonDisabled}
              onClick={() =>
                handleNavButtonClick({
                  activeButtonRef: rightButtonRef,
                  inactiveButtonRef: leftButtonRef,
                  newSlideIndex: slideIndex + 1,
                })
              }
              isLightMode={isLightMode}
            />
          </motion.div>
        </div>
      </div>

      <ShadowContainerHorizontal
        showStart={
          shadows &&
          (slideIndex > 0 || shouldLoop) &&
          shouldDisplayShadowsOnBreakpoint
        }
        showEnd={
          shadows &&
          (slideIndex < itemsToDisplay.length - 1 || shouldLoop) &&
          shouldDisplayShadowsOnBreakpoint
        }
        shadowColour={shadowColour}
        disableScroll
      >
        <div
          className={classNames(
            'whitespace-normal',
            isScrollAnimationEnabled ? 'container' : horizontalSpacingClassName
          )}
        >
          <Carousel
            spaceBetween={spaceBetween}
            loop={shouldLoop}
            navId={id}
            className={classNames('w-full', {
              'overflow-visible': isScrollAnimationEnabled,
              'overflow-xl':
                desktopBreakpoint === BreakpointEnum.XLarge &&
                !isScrollAnimationEnabled &&
                isOverflowHidden,
              'overflow-mobile': desktopBreakpoint === BreakpointEnum.Medium,
              [`${initialTabletSlidesClassname} ${initialDesktopSlidesClassname} md:[&>.swiper-wrapper>.swiper-slide]:pr-5`]:
                !isSwiperInitialized,
            })}
            onResize={handleSwiperUpdate}
            onInit={(swiper) => {
              handleSwiperUpdate(swiper);
              setIsSwiperInitialized(true);
            }}
            onSwiper={setSwiperInstance}
            watchSlidesProgress
            breakpoints={{
              0: {
                slidesPerView: slidesToShowMobile,
              },
              768: {
                slidesPerView: slidesToShowTablet,
              },
              1440: {
                slidesPerView: slidesToShowDesktop,
              },
              ...additionalBreakpoints,
            }}
            onSlideChange={(swiper) => {
              setSlideIndex(swiper?.realIndex || 0);
            }}
            freeMode={isScrollAnimationEnabled}
            additionalModules={isScrollAnimationEnabled ? [FreeMode] : []}
          >
            {children(itemsToDisplay)}
          </Carousel>
        </div>
      </ShadowContainerHorizontal>
    </div>
  );
};

export default NavigationCarousel;
